import type {
  Bill,
  BillListItem,
  CreateBillRequest,
  BillInput,
  ReceiptDraft,
  RelinkBillPersonRequest,
} from "@boo/shared";
import { request } from "./apiClient";

export async function fetchBills(): Promise<BillListItem[]> {
  return request<BillListItem[]>("/bills");
}

export async function fetchBill(id: string): Promise<Bill> {
  return request<Bill>(`/bills/${id}`);
}

export async function parseReceipt(file: File): Promise<ReceiptDraft> {
  const formData = new FormData();
  formData.append("file", file);
  return request<ReceiptDraft>("/bills/receipt", {
    method: "POST",
    data: formData,
  });
}

export async function createBill(payload: CreateBillRequest): Promise<Bill> {
  return request<Bill>("/bills", { method: "POST", data: payload });
}

export async function saveBill(id: string, payload: BillInput): Promise<Bill> {
  return request<Bill>(`/bills/${id}`, { method: "PUT", data: payload });
}

export async function finalizeBill(id: string): Promise<Bill> {
  return request<Bill>(`/bills/${id}/finalize`, { method: "POST" });
}

export async function reopenBill(id: string): Promise<Bill> {
  return request<Bill>(`/bills/${id}/reopen`, { method: "POST" });
}

export async function deleteBill(id: string): Promise<void> {
  await request<void>(`/bills/${id}`, { method: "DELETE" });
}

export async function setBillPaymentProfile(
  id: string,
  paymentProfileId: string | null,
): Promise<Bill> {
  return request<Bill>(`/bills/${id}/payment-profile`, {
    method: "PATCH",
    data: { paymentProfileId },
  });
}

export async function createBillShare(id: string): Promise<Bill> {
  return request<Bill>(`/bills/${id}/share`, { method: "POST" });
}

export async function revokeBillShare(id: string): Promise<Bill> {
  return request<Bill>(`/bills/${id}/share`, { method: "DELETE" });
}

export async function uploadBillReceipt(id: string, file: File): Promise<Bill> {
  const formData = new FormData();
  formData.append("file", file);
  return request<Bill>(`/bills/${id}/receipt`, {
    method: "POST",
    data: formData,
  });
}

export async function resolveBillFlag(
  id: string,
  flagId: string,
): Promise<Bill> {
  return request<Bill>(`/bills/${id}/flags/${flagId}`, { method: "DELETE" });
}

export async function fetchBillReceiptUrl(id: string): Promise<string> {
  const blob = await request<Blob>(`/bills/${id}/receipt`, {
    responseType: "blob",
  });
  return URL.createObjectURL(blob);
}

export async function confirmBillSlip(
  id: string,
  slipId: string,
): Promise<Bill> {
  return request<Bill>(`/bills/${id}/slips/${slipId}/confirm`, {
    method: "POST",
  });
}

export async function markBillPersonPaid(
  id: string,
  personId: string,
): Promise<Bill> {
  return request<Bill>(`/bills/${id}/people/${personId}/mark-paid`, {
    method: "POST",
  });
}

export async function rejectBillSlip(
  id: string,
  slipId: string,
): Promise<Bill> {
  return request<Bill>(`/bills/${id}/slips/${slipId}/reject`, {
    method: "POST",
  });
}

export async function fetchBillSlipUrl(
  id: string,
  slipId: string,
): Promise<string> {
  const blob = await request<Blob>(`/bills/${id}/slips/${slipId}/image`, {
    responseType: "blob",
  });
  return URL.createObjectURL(blob);
}

export async function relinkBillPerson(
  id: string,
  personId: string,
  friendId: string,
): Promise<Bill> {
  return request<Bill>(`/bills/${id}/people/${personId}/friend`, {
    method: "PATCH",
    data: { friendId } satisfies RelinkBillPersonRequest,
  });
}
